/**
 * The GUC sizer's Review-mode content: the KPI strip a builder-lender reads
 * first (required coupon, the three amounts in build-up order, the verdict and
 * liquidity) and the lean result cards laid out wide.
 *
 * Render-only, like GucResultsPanel: the rate, eligibility, lock readiness and
 * liquidity are response fields, and the three amounts are what the backend's
 * normalizer wrote onto the STORED scenario for this version — read through
 * `pricedAmounts`, the same accessor the Edit rail's amounts strip uses.
 * Nothing is recomputed here.
 */
import Box from "@mui/material/Box";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { SZ } from "../../sizersShared/sizerUi";
import { Callout } from "../../sizersShared/sizerResultUi";
import {
  fmtMoney,
  fmtPct,
  fmtRate,
  fmtRateDec,
} from "../../sizersShared/sizerFormat";
import {
  rtlBuyRate,
  rtlRequiredCoupon,
} from "../../sizersShared/rtlRateDisplay";
import {
  KpiCell,
  KpiHero,
  KpiStrip,
} from "../../sizersShared/workspace/ScenarioReview";
import { WsSecondaryButton } from "../../sizersShared/workspace/workspaceParts";
import { rtlRateChips } from "../../SizerScreen/components/RtlResultsColumn";
import { pricedAmounts } from "../gucLoanMath";
import GucResultsPanel from "./GucResultsPanel";
import { ExplainCard } from "../../sizersShared/workspace/ExplainResultPanel";

const pctOf = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n > 1 ? n / 100 : n;
};

export function GucKpis({ result, scenario, version, amounts: amountsProp }) {
  const rate = result?.rate || {};
  const req = result?.requirements || {};
  const s = scenario || {};
  const resolved = result?.resolved || {};
  const cls = resolved.borrowerClass || resolved.classification?.borrowerClass;
  // The caller's amounts (priced, or the estimate while the stored scenario
  // loads) win; standalone, read them off the scenario.
  const amounts = amountsProp || pricedAmounts(result, scenario);
  const coupon = rate.coupon ?? rtlRequiredCoupon(rate);
  const buy = rtlBuyRate(rate);
  const lev = pctOf(s.sizerInitialRequestedLeverage);
  const financed = pctOf(s.financedBudgetPercent);
  const over = (result?.leverage || []).filter((r) => r.over).length;

  return (
    <KpiStrip
      hero={
        <KpiHero
          label={`Required coupon${version ? ` · v${version}` : ""}`}
          value={rate.error ? "—" : fmtRateDec(coupon)}
          sub={
            rate.error
              ? rate.error
              : [
                  buy != null ? `Buy rate ${fmtRate(buy)}` : null,
                  cls ? `Class ${cls}` : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || null
          }
          chips={rate.error ? [] : rtlRateChips(rate)}
        />
      }
    >
      <KpiCell
        label="Total loan"
        value={fmtMoney(amounts?.total)}
        caption={[s.termMonths ? `${s.termMonths} mo` : null, s.loanPurpose]
          .filter(Boolean)
          .join(" · ")}
      />
      <KpiCell
        label="Initial advance"
        value={fmtMoney(amounts?.advance)}
        caption={
          lev != null
            ? `${fmtPct(lev, 0)} requested leverage`
            : amounts?.advance
              ? "Set amount, not leverage"
              : null
        }
      />
      <KpiCell
        label="Construction holdback"
        value={fmtMoney(amounts?.holdback)}
        caption={financed != null ? `${fmtPct(financed, 0)} of budget` : null}
      />
      <KpiCell
        label="Guidelines"
        value={
          result?.eligible === false
            ? "Not eligible"
            : over > 0
              ? `${over} over`
              : "Eligible"
        }
        tone={result?.eligible === false ? "bad" : over > 0 ? "warn" : "good"}
        caption={
          result ? (result.lockReady ? "Lock ready" : "Not lock ready") : null
        }
      />
      <KpiCell
        label="Liquidity Coverage"
        value={
          req.liquidityCovered == null
            ? "—"
            : req.liquidityCovered
              ? "Sufficient Liquidity"
              : "Short"
        }
        tone={
          req.liquidityCovered == null
            ? null
            : req.liquidityCovered
              ? "good"
              : "bad"
        }
        caption={
          req.requiredLiquidity != null
            ? `Needs ${fmtMoney(req.requiredLiquidity)}${
                req.liquidityCovered === false && req.liquidityShortfall != null
                  ? ` · short ${fmtMoney(req.liquidityShortfall)}`
                  : ""
              }`
            : null
        }
      />
    </KpiStrip>
  );
}

/**
 * The zero-ground-up-experience blocker, softened while a request is open —
 * spec §4. It stays a note rather than a green light: nothing is approved yet.
 * Shared by the Edit rail and Review mode so the two say the same thing.
 */
export function ZeroExperienceNote({ pending, inReview }) {
  return pending ? (
    <Callout tone="warn" title="Requested — pending approval">
      A GC completion guaranty exception has been filed against this scenario
      and is waiting on approval. The leverage grid stays unavailable until it
      is approved.
    </Callout>
  ) : (
    <Callout tone="bad" title="No ground-up track record">
      Ground-up classifies on completed projects alone, and the grid carries no
      class C row — so there is no credit box to price against. Turn on the GC
      completion guaranty in Loan details
      {inReview ? " (Edit inputs)" : ""} to open the exception request.
    </Callout>
  );
}

/**
 * The lean result cards, wide. The "Explain this result" way in sits on top:
 * in Edit mode it lives on the amounts strip, which Review replaces with the
 * KPI strip — and the dialog is where every finding's Request-exception button
 * is, so Review must not lose it.
 */
export function GucPanels({
  result,
  cures,
  onRepriceAt,
  onApplyBudgetShare,
  onExplain,
  blocker,
}) {
  return (
    <Flx column g={1.75}>
      <ExplainCard
        onExplain={onExplain}
        description="Every finding with its citation and a Request exception button, the rate build-up, and every reprice option the engine returned."
      />
      {blocker}
      <GucResultsPanel
        result={result}
        cures={cures}
        onRepriceAt={onRepriceAt}
        onApplyBudgetShare={onApplyBudgetShare}
        masonry
      />
    </Flx>
  );
}
